<template>
  <div class="p-3 pt-5 rounded bg-body mb-5">
    <div class="row align-items-end mb-5">
      <div class="col-lg-2 mb-5 mb-lg-0">
        <date-field
          v-model="filters.fromDate"
          id="ticketsRealization.fromDate"
          name="ticketsRealization.fromDate"
          placeholder="Дата с"
        />
      </div>
      <div class="col-lg-2 mb-5 mb-lg-0">
        <date-field
          v-model="filters.toDate"
          id="ticketsRealization.toDate"
          name="ticketsRealization.toDate"
          placeholder="Дата по"
        />
      </div>
      <div class="col-lg-3 ms-auto d-flex justify-content-end gap-2 mb-5 mb-lg-0">
        <button type="button" class="btn btn-sm btn-primary" @click="applyFilters">
          Показать
        </button>
        <button type="button" class="btn btn-sm btn-light-danger" @click="resetFilters">
          Сбросить
        </button>
      </div>
    </div>
    <loader v-if="loaded" :height="25" />
    <div v-show="!loaded">
      <apexchart
        type="bar"
        height="350"
        :options="chartOptions"
        :series="series"
      />
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts'
import Loader from '@/components/Loader'
import DateField from '@/components/Fields/DateField'
import axios from 'axios'

export default {
  name: 'TicketsRealization',
  components: {
    Loader,
    DateField,
    apexchart: VueApexCharts
  },
  data() {
    return {
      loaded: false,
      defaultFromDate: '',
      defaultToDate: '',
      filters: {
        fromDate: '',
        toDate: ''
      },
      series: [],
      chartOptions: {
        chart: {
          type: 'bar',
          height: 350,
          stacked: true,
          toolbar: { show: true },
          zoom: { enabled: true }
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: {
                position: 'bottom',
                offsetX: -10,
                offsetY: 0
              }
            }
          }
        ],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            borderRadius: 10,
            borderRadiusApplication: 'end',
            borderRadiusWhenStacked: 'last',
            dataLabels: {
              total: {
                enabled: false,
                style: {
                  fontSize: '13px',
                  fontWeight: 900
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          categories: [],
          labels: {
            style: { fontSize: '12px' }
          }
        },
        yaxis: {
          title: { text: 'Количество' },
          labels: {
            formatter: val => {
              return parseInt(val)
            }
          }
        },
        legend: {
          position: 'right',
          offsetY: 40
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter(val, opts) {
              return `${parseInt(val)}`
            }
          }
        },
        title: {
          text: 'Продажа абонемента',
          align: 'left'
        }
      }
    }
  },
  mounted() {
    this.setDefaultDates()
    this.getTicketsRealization()
  },
  methods: {
    toYmd(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    setDefaultDates() {
      // дефолт: ровно месяц (как было)
      const to = new Date()
      const from = new Date(to)
      from.setMonth(to.getMonth() - 1)

      this.defaultFromDate = this.toYmd(from)
      this.defaultToDate = this.toYmd(to)

      this.filters.fromDate = this.defaultFromDate
      this.filters.toDate = this.defaultToDate
    },
    normalizeRange(fromDateStr, toDateStr) {
      const fromStr = fromDateStr || this.defaultFromDate
      const toStr = toDateStr || this.defaultToDate

      const from = new Date(`${fromStr}T00:00:00`)
      const to = new Date(`${toStr}T00:00:00`)

      if (isNaN(from.getTime()) || isNaN(to.getTime())) {
        return { fromDate: fromStr, toDate: toStr }
      }

      if (from.getTime() <= to.getTime()) {
        return { fromDate: this.toYmd(from), toDate: this.toYmd(to) }
      }

      return { fromDate: this.toYmd(to), toDate: this.toYmd(from) }
    },
    formatAxisDate(dateValue) {
      if (!dateValue) return ''
      const d = new Date(dateValue)
      if (isNaN(d.getTime())) return String(dateValue)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      return `${day}.${month}`
    },
    formatAmount(val) {
      const formatted = Number(val || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 })
      return formatted.replace(/\u00A0/g, ' ')
    },
    applyFilters() {
      this.getTicketsRealization()
    },
    resetFilters() {
      this.filters.fromDate = this.defaultFromDate
      this.filters.toDate = this.defaultToDate
      this.getTicketsRealization()
    },
    getTicketsRealization() {
      this.loaded = true

      const { fromDate, toDate } = this.normalizeRange(
        this.filters.fromDate,
        this.filters.toDate
      )

      axios.get('order-service/api/v1/admin/statistic/tickets/days/charts/stacked', {
        params: { fromDate, toDate }
      })
        .then(res => {
          const payload = res?.data && typeof res.data === 'object' ? res.data : {}
          const dates = Array.isArray(payload.dates) ? payload.dates : []
          const countSeries = Array.isArray(payload.countSeries) ? payload.countSeries : []
          const totalAmountSeries = Array.isArray(payload.totalAmountSeries) ? payload.totalAmountSeries : []

          if (!dates.length || !countSeries.length) {
            this.series = []
            this.chartOptions = {
              ...this.chartOptions,
              title: { ...this.chartOptions.title, text: `Абонименты с ${fromDate} по ${toDate}` }
            }
            return
          }

          // Для tooltip: быстро находить сумму по specId/specName
          const amountBySpecId = new Map(
            totalAmountSeries.map(s => [s?.specId, Array.isArray(s?.data) ? s.data : []])
          )

          const categories = dates.map(d => this.formatAxisDate(d))

          // Stacked Columns: категории = даты, серии = специалисты (specName), данные = countSeries[i].data[]
          const series = countSeries.map(s => ({
            name: s?.specName || s?.specId || '—',
            data: Array.isArray(s?.data) ? s.data : []
          }))

          this.series = series
          this.chartOptions = {
            ...this.chartOptions,
            xaxis: { ...this.chartOptions.xaxis, categories },
            tooltip: {
              ...this.chartOptions.tooltip,
              y: {
                formatter: (val, opts) => {
                  const count = parseInt(val || 0)
                  const seriesIndex = opts?.seriesIndex ?? 0
                  const dataPointIndex = opts?.dataPointIndex ?? 0
                  const specId = countSeries?.[seriesIndex]?.specId
                  const amountArr = amountBySpecId.get(specId) || []
                  const amount = amountArr?.[dataPointIndex] ?? 0
                  return `${count} (сумма: ${this.formatAmount(amount)})`
                }
              }
            },
            title: {
              ...this.chartOptions.title,
              text: `Продажа абонемента по дням с ${fromDate} по ${toDate}`
            }
          }
        })
        .catch(err => {
          console.error('Ошибка при запросе getTicketsRealization:', err)
        })
        .finally(() => {
          this.loaded = false
        })
    }

  }
}
</script>
